import { UserRole } from "constants/userRole";
import { useUserWithAccount } from "context/UserAccountProvider";
import { useMemo } from "react";
import {
  useGetAllProvidersQuery,
  useGetProviderOptionsByAccountQuery,
} from "store/queries/providers";
import { IProviderOption, SpecialtyList } from "types/Provider/Provider";

export type IContractProvider = IProviderOption & { accountId?: string | null };

export interface IContractProviders {
  data: IContractProvider[] | undefined;
  /** True when `data` is already one account's providers, so it must not be filtered on `accountId`. */
  isAccountScoped: boolean;
}

/**
 * CLE-2892 — the provider list behind the contract screens' provider pickers: ContractsTable's provider
 * filter, and the provider and connected-providers pickers on CreateContract / EditContract.
 *
 * Nothing changes for anyone these screens already worked for: a SuperAdmin and a HospitalStaff user who is
 * not contracts-only read `GET /providers`, as before — every provider they can access, across accounts, which
 * the connected-providers picker needs (CLE-2786). A contracts-only HospitalStaff user got a 404 there (the
 * full provider rows stay gated), so they read the slim list of the account they are in instead
 * (`GET /providers/account/:id/options` — id, name and specialty).
 *
 * @param accountId - the account the screen is in; without one, a contracts-only user gets no list
 * @param contractProvider - on the edit page, the contract's own provider. A shared contract (CLE-2786) can
 *   belong to a provider outside the account being viewed, so it is added to an account-scoped list.
 */
export function useContractProviders(
  accountId: string | null | undefined,
  contractProvider?: { id: string; name: string; specialty: string }
): IContractProviders {
  const { userInfo } = useUserWithAccount();
  const readsAccountOptions =
    userInfo?.scope !== UserRole.SUPER_ADMIN &&
    Boolean(userInfo?.contractsOnlyMode);

  const { data: allProviders } = useGetAllProvidersQuery(undefined, {
    skip: readsAccountOptions,
  });
  const { data: accountProviders } = useGetProviderOptionsByAccountQuery(
    { accountId: accountId ?? "" },
    { skip: !readsAccountOptions || !accountId }
  );

  return useMemo(() => {
    if (!readsAccountOptions) {
      return { data: allProviders?.data, isAccountScoped: false };
    }
    const data = accountProviders?.data;
    if (
      !data ||
      !contractProvider ||
      data.some((provider) => provider.id === contractProvider.id)
    ) {
      return { data, isAccountScoped: true };
    }
    return {
      data: [
        ...data,
        {
          id: contractProvider.id,
          name: contractProvider.name,
          specialty: contractProvider.specialty as SpecialtyList,
        },
      ],
      isAccountScoped: true,
    };
  }, [readsAccountOptions, allProviders, accountProviders, contractProvider]);
}
